import { Routes } from '@angular/router';
import { authGuard, guestGuard } from './core/auth.guard';
import { roleGuard } from './core/role.guard';
import { CALENDAR_ROLES } from './features/calendar/calendar-access';
import { PLAN_ROLES } from './features/preventive-maintenance/plan-access';

export const routes: Routes = [
  { path: 'login', canActivate: [guestGuard], loadComponent: () => import('./features/login/login.component').then((m) => m.LoginComponent) },
  {
    path: 'register',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/register-organization/register-organization.component').then((m) => m.RegisterOrganizationComponent)
  },
  {
    path: 'forgot-password',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent)
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./features/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent)
  },
  {
    path: 'sso-callback',
    loadComponent: () => import('./features/sso-callback/sso-callback.component').then((m) => m.SsoCallbackComponent)
  },
  {
    path: 'organizations/claim',
    loadComponent: () => import('./features/claim-organization/claim-organization.component').then((m) => m.ClaimOrganizationComponent)
  },
  {
    path: 'dashboard',
    canActivate: [authGuard],
    loadComponent: () => import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent)
  },
  {
    path: 'calendar',
    canActivate: [authGuard, roleGuard],
    data: { roles: CALENDAR_ROLES },
    loadComponent: () => import('./features/calendar/calendar.component').then((m) => m.CalendarComponent)
  },
  {
    path: 'preventive-maintenance',
    canActivate: [authGuard, roleGuard],
    data: { roles: PLAN_ROLES },
    loadComponent: () =>
      import('./features/preventive-maintenance/preventive-maintenance.component').then((m) => m.PreventiveMaintenanceComponent)
  },
  {
    path: 'preventive-maintenance/new',
    canActivate: [authGuard, roleGuard],
    data: { roles: PLAN_ROLES },
    loadComponent: () =>
      import('./features/preventive-maintenance/form/maintenance-plan-form.component').then((m) => m.MaintenancePlanFormComponent)
  },
  {
    path: 'preventive-maintenance/:id/edit',
    canActivate: [authGuard, roleGuard],
    data: { roles: PLAN_ROLES },
    loadComponent: () =>
      import('./features/preventive-maintenance/form/maintenance-plan-form.component').then((m) => m.MaintenancePlanFormComponent)
  },
  {
    path: 'preventive-maintenance/:id',
    canActivate: [authGuard, roleGuard],
    data: { roles: PLAN_ROLES },
    loadComponent: () =>
      import('./features/preventive-maintenance/detail/maintenance-plan-detail.component').then((m) => m.MaintenancePlanDetailComponent)
  },
  {
    path: 'work-orders',
    canActivate: [authGuard],
    loadComponent: () => import('./features/work-orders/work-orders.component').then((m) => m.WorkOrdersComponent)
  },
  {
    path: 'work-orders/new',
    canActivate: [authGuard],
    loadComponent: () => import('./features/work-orders/new/new-work-order.component').then((m) => m.NewWorkOrderComponent)
  },
  {
    path: 'work-orders/:id',
    canActivate: [authGuard],
    loadComponent: () => import('./features/work-order-detail/work-order-detail.component').then((m) => m.WorkOrderDetailComponent)
  },
  {
    path: 'maintenance-requests',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/maintenance-requests/maintenance-requests.component').then((m) => m.MaintenanceRequestsComponent)
  },
  {
    path: 'maintenance-requests/new',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/maintenance-requests/new/new-maintenance-request.component').then((m) => m.NewMaintenanceRequestComponent)
  },
  {
    path: 'maintenance-requests/:id',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/maintenance-requests/detail/maintenance-request-detail.component').then((m) => m.MaintenanceRequestDetailComponent)
  },
  {
    path: 'parts',
    canActivate: [authGuard],
    loadComponent: () => import('./features/parts/parts.component').then((m) => m.PartsComponent)
  },
  {
    path: 'parts/new',
    canActivate: [authGuard],
    loadComponent: () => import('./features/parts/new/new-part.component').then((m) => m.NewPartComponent)
  },
  {
    path: 'parts/categories',
    canActivate: [authGuard],
    loadComponent: () => import('./features/parts/categories/part-categories.component').then((m) => m.PartCategoriesComponent)
  },
  {
    path: 'parts/:id',
    canActivate: [authGuard],
    loadComponent: () => import('./features/parts/detail/part-detail.component').then((m) => m.PartDetailComponent)
  },
  {
    path: 'stores',
    canActivate: [authGuard],
    loadComponent: () => import('./features/stores/stores.component').then((m) => m.StoresComponent)
  },
  {
    path: 'stores/new',
    canActivate: [authGuard],
    loadComponent: () => import('./features/stores/new/new-store.component').then((m) => m.NewStoreComponent)
  },
  {
    path: 'stores/:id',
    canActivate: [authGuard],
    loadComponent: () => import('./features/stores/detail/store-detail.component').then((m) => m.StoreDetailComponent)
  },
  {
    path: 'inventory',
    canActivate: [authGuard],
    loadComponent: () => import('./features/inventory/inventory.component').then((m) => m.InventoryComponent)
  },
  {
    path: 'inventory/usage-report',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/inventory/usage-report/usage-report.component').then((m) => m.InventoryUsageReportComponent)
  },
  {
    path: 'inventory/transactions',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/inventory/transactions/stock-transactions.component').then((m) => m.StockTransactionsComponent)
  },
  {
    path: 'inventory/adjustments',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/inventory/adjustment/stock-adjustment.component').then((m) => m.StockAdjustmentComponent)
  },
  {
    path: 'purchase-orders',
    canActivate: [authGuard],
    loadComponent: () => import('./features/purchase-orders/purchase-orders.component').then((m) => m.PurchaseOrdersComponent)
  },
  {
    path: 'purchase-orders/new',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/purchase-orders/new/new-purchase-order.component').then((m) => m.NewPurchaseOrderComponent)
  },
  {
    path: 'purchase-orders/:id',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/purchase-orders/detail/purchase-order-detail.component').then((m) => m.PurchaseOrderDetailComponent)
  },
  {
    path: 'jobs',
    canActivate: [authGuard],
    loadComponent: () => import('./features/jobs/jobs.component').then((m) => m.JobsComponent)
  },
  {
    path: 'jobs/:id',
    canActivate: [authGuard],
    loadComponent: () => import('./features/jobs/detail/job-detail.component').then((m) => m.JobDetailComponent)
  },
  {
    path: 'my-approvals',
    canActivate: [authGuard],
    loadComponent: () => import('./features/my-approvals/my-approvals.component').then((m) => m.MyApprovalsComponent)
  },
  {
    // The notification deep link: same page, with that task highlighted above the list.
    path: 'my-approvals/:taskId',
    canActivate: [authGuard],
    loadComponent: () => import('./features/my-approvals/my-approvals.component').then((m) => m.MyApprovalsComponent)
  },
  {
    path: 'my-tasks',
    canActivate: [authGuard],
    loadComponent: () => import('./features/my-tasks/my-tasks.component').then((m) => m.MyTasksComponent)
  },
  {
    path: 'service-contracts',
    canActivate: [authGuard],
    loadComponent: () => import('./features/service-contracts/service-contracts.component').then((m) => m.ServiceContractsComponent)
  },
  {
    path: 'vendors',
    canActivate: [authGuard],
    loadComponent: () => import('./features/vendors/vendors.component').then((m) => m.VendorsComponent)
  },
  {
    path: 'vendors/new',
    canActivate: [authGuard],
    loadComponent: () => import('./features/vendors/new/new-vendor.component').then((m) => m.NewVendorComponent)
  },
  {
    path: 'vendors/:id',
    canActivate: [authGuard],
    loadComponent: () => import('./features/vendors/detail/vendor-detail.component').then((m) => m.VendorDetailComponent)
  },
  {
    path: 'assets',
    canActivate: [authGuard],
    loadComponent: () => import('./features/assets/assets.component').then((m) => m.AssetsComponent)
  },
  {
    path: 'assets/new',
    canActivate: [authGuard],
    loadComponent: () => import('./features/assets/new/new-asset.component').then((m) => m.NewAssetComponent)
  },
  {
    path: 'assets/:id',
    canActivate: [authGuard],
    loadComponent: () => import('./features/assets/detail/asset-detail.component').then((m) => m.AssetDetailComponent)
  },
  {
    path: 'admin/buildings',
    canActivate: [authGuard],
    loadComponent: () => import('./features/admin/buildings/buildings.component').then((m) => m.BuildingsComponent)
  },
  {
    path: 'admin/buildings/new',
    canActivate: [authGuard],
    loadComponent: () => import('./features/admin/buildings/new/new-building.component').then((m) => m.NewBuildingComponent)
  },
  {
    path: 'admin/buildings/:id',
    canActivate: [authGuard],
    loadComponent: () => import('./features/admin/buildings/detail/building-detail.component').then((m) => m.BuildingDetailComponent)
  },
  {
    path: 'admin/priority-matrix',
    canActivate: [authGuard],
    loadComponent: () => import('./features/admin/priority-matrix/priority-matrix.component').then((m) => m.PriorityMatrixComponent)
  },
  {
    path: 'admin/maintenance-categories',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/admin/maintenance-categories/maintenance-categories.component').then((m) => m.MaintenanceCategoriesComponent)
  },
  {
    path: 'admin/users',
    canActivate: [authGuard],
    loadComponent: () => import('./features/admin/users/admin-users.component').then((m) => m.AdminUsersComponent)
  },
  {
    path: 'admin/users/new',
    canActivate: [authGuard],
    loadComponent: () => import('./features/admin/users/new/new-user.component').then((m) => m.NewUserComponent)
  },
  {
    path: 'admin/users/:id',
    canActivate: [authGuard],
    loadComponent: () => import('./features/admin/users/detail/admin-user-detail.component').then((m) => m.AdminUserDetailComponent)
  },
  {
    path: 'admin/audit-log',
    canActivate: [authGuard],
    loadComponent: () => import('./features/admin/audit-log/audit-log.component').then((m) => m.AuditLogComponent)
  },
  {
    path: 'admin/imports',
    canActivate: [authGuard],
    loadComponent: () => import('./features/admin/imports/imports.component').then((m) => m.ImportsComponent)
  },
  {
    path: 'admin/notification-templates/new',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/admin/notification-templates/form/notification-template-form.component').then((m) => m.NotificationTemplateFormComponent)
  },
  {
    path: 'admin/notification-templates/:id/edit',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/admin/notification-templates/form/notification-template-form.component').then((m) => m.NotificationTemplateFormComponent)
  },
  {
    path: 'admin/notification-templates',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/admin/notification-templates/notification-templates.component').then((m) => m.NotificationTemplatesComponent)
  },
  {
    path: 'admin/notification-log',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/admin/notification-log/notification-log.component').then((m) => m.NotificationLogComponent)
  },
  {
    path: 'settings',
    canActivate: [authGuard],
    loadComponent: () => import('./features/settings/settings.component').then((m) => m.SettingsComponent)
  },
  {
    path: 'admin/organizations',
    canActivate: [authGuard],
    loadComponent: () => import('./features/admin/organizations/organizations.component').then((m) => m.OrganizationsComponent)
  },
  {
    path: 'admin/organizations/:id',
    canActivate: [authGuard],
    loadComponent: () => import('./features/admin/organizations/detail/organization-detail.component').then((m) => m.OrganizationDetailComponent)
  },
  {
    path: 'admin/agents',
    canActivate: [authGuard],
    loadComponent: () => import('./features/admin/agents/agents.component').then((m) => m.AgentsComponent)
  },
  {
    path: 'admin/approval-routing',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/admin/approval-routing/list/approval-routing-list.component').then((m) => m.ApprovalRoutingListComponent)
  },
  {
    path: 'admin/approval-routing/:actionKey',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/admin/approval-routing/detail/approval-routing-detail.component').then(
        (m) => m.ApprovalRoutingDetailComponent
      )
  },
  {
    path: '',
    pathMatch: 'full',
    canActivate: [authGuard],
    loadComponent: () => import('./features/home/home-redirect.component').then((m) => m.HomeRedirectComponent)
  },
  { path: '**', redirectTo: '' }
];
